import { Card as HeroCard, Skeleton as HeroSkeleton } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Card, PageLayout, Skeleton } from '@/shared/ui';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { cn } from '@/shared/lib/cn';

/**
 * Экран, пока грузится чанк «Аналитики» (`lazyPage` в `app/router`), и
 * скелеты двух её блоков — тех же, что каждый блок показывает, пока ждёт
 * свои данные. Страница и скелет рисуют их одним кодом, поэтому загрузка кода
 * и данных идёт одним шиммером.
 *
 * Отдельным файлом ещё и потому, что графики тянут recharts: скелеты графиков
 * раньше жили рядом с ними и в стартовый чанк не годились.
 */
export function StatisticsPageSkeleton() {
  const { t } = useTranslation('StatisticsPage');
  const { t: tAnalytics } = useTranslation('AnalyticsSection');
  const { t: tLevel } = useTranslation('LevelSection');
  return (
    <PageLayout title={t('title')}>
      <ProgressSkeleton tileCount={6} />
      {/* Та же карточка и шапка, что у `AnalyticsSection` до первых данных. */}
      <Card className="flex flex-col gap-5 bg-transparent px-3 py-5 shadow-none">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-xl font-extrabold text-ink">{tAnalytics('title')}</h2>
          <Skeleton className="h-7 w-20 shrink-0 rounded-full" />
        </div>
        <AnalyticsChartsSkeleton />
      </Card>
      {/* Та же карточка и шапка, что у `LevelSection` до первых данных. */}
      <Card className="flex flex-col gap-5 bg-transparent px-3 py-5 shadow-none">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-extrabold text-ink">{tLevel('title')}</h2>
          <Skeleton className="h-4 w-48" />
        </div>
        <LevelSectionSkeleton />
      </Card>
    </PageLayout>
  );
}

/** Высота графиков «Аналитики» — общая для графиков и их скелетов. */
export const CHART_HEIGHT = 180;

/** Форма карточки «Прогресс» — сетка из 6 тайлов, а не общий прямоугольник: так шиммер не обещает контент шире, чем придёт. */
export function ProgressSkeleton({ tileCount }: { tileCount: number }) {
  const inDesktopShell = useInDesktopShell();
  const { t } = useTranslation('StatisticsPage');
  return (
    <HeroCard className="flex-col gap-5 px-3 py-5" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('loadingProgress')}</span>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-extrabold text-ink">{t('progressTitle')}</h2>
        <p className="text-[13px] text-ink-muted">{t('progressSubtitle')}</p>
      </div>
      <div className={cn('grid gap-3', inDesktopShell ? 'grid-cols-3' : 'grid-cols-2')}>
        {Array.from({ length: tileCount }, (_, index) => (
          <HeroSkeleton
            key={index}
            className="h-[6.5rem] rounded-2xl border border-hairline shadow-[0_6px_16px_rgba(0,0,0,0.12)]"
          />
        ))}
      </div>
    </HeroCard>
  );
}

export function AnalyticsChartsSkeleton() {
  const { t } = useTranslation('AnalyticsSection');
  return (
    <div aria-busy="true" aria-live="polite" className="flex flex-col gap-4">
      <span className="sr-only">{t('loadingCharts')}</span>
      <CorrectnessChartSkeleton />
      <PracticeTimeChartSkeleton />
    </div>
  );
}

function CorrectnessChartSkeleton() {
  const { t } = useTranslation('CorrectnessChart');
  return (
    <Card className="flex flex-col gap-1" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('loadingChart')}</span>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink-muted">{t('title')}</span>
        <Skeleton className="h-6 w-12" />
      </div>
      <Skeleton className="mt-2" style={{ height: CHART_HEIGHT }} />
    </Card>
  );
}

function PracticeTimeChartSkeleton() {
  const { t } = useTranslation('PracticeTimeChart');
  return (
    <Card className="flex flex-col gap-1" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('loadingChart')}</span>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink-muted">{t('title')}</span>
        <Skeleton className="h-6 w-16" />
      </div>
      <Skeleton className="mt-2" style={{ height: CHART_HEIGHT }} />
    </Card>
  );
}

/** Лента месяцев и карточка с радаром — форма блока «Уровень языка» до данных. */
export function LevelSectionSkeleton() {
  const { t } = useTranslation('LevelSection');
  return (
    <div aria-busy="true" aria-live="polite" className="flex flex-col gap-5">
      <span className="sr-only">{t('loading')}</span>
      <div className="flex gap-2">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-24 rounded-full" />
        ))}
      </div>
      <Card className="flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <Skeleton className="h-8 w-14" />
          <Skeleton className="mt-1 h-5 w-24" />
        </div>
        <Skeleton className="mx-auto mt-6 mb-2 aspect-square w-full max-w-64 rounded-full" />
      </Card>
    </div>
  );
}
